/**
 * forge.ts — 용광로(조합/교환) 시스템 (Sprint 2)
 *
 * 레시피 정의 + 조합 실행 순수 로직.
 * 브라우저 API / PIXI / localStorage 절대 금지.
 */

import type { TranslationKey } from './i18n/en.js';
import type { Materials } from './materials.js';

// ──────────────────────────────────────────────
//  레시피 타입
// ──────────────────────────────────────────────

export type ForgeCategory = 'weapon' | 'scroll' | 'upgrade' | 'item';

/** 재료 비용 (각 재료 키별 필요 개수) */
export interface MaterialCost {
    iron_dust?: number;
    refining_core?: number;
    lava_essence?: number;
    abyss_crystal?: number;
}

/** 조합 결과 */
export type ForgeResultKind =
    | { type: 'weapon'; level: number }            // 무기 획득 (Lv.N)
    | { type: 'scroll'; tier: number; count: number }  // 방지권 (tier=0..3)
    | { type: 'material_upgrade'; from: keyof Materials; to: keyof Materials; gain: number } // 재료 승급
    | { type: 'buff_item'; item: 'luck_charm' | 'guarantee' | 'destroy_guard'; count: number }; // 버프 아이템

export interface ForgeRecipe {
    id: string;
    /** i18n 레이블 키 */
    nameKey: TranslationKey;
    /** i18n 설명 키 (선택) */
    descKey?: TranslationKey;
    /** FLUX 아이콘 경로 (public/items/ 하위) */
    icon: string;
    /** 카테고리 */
    category: ForgeCategory;
    /** 재료 비용 */
    cost: MaterialCost;
    /** 결과 */
    result: ForgeResultKind;
}

// ──────────────────────────────────────────────
//  레시피 목록
// ──────────────────────────────────────────────

export const FORGE_RECIPES: ForgeRecipe[] = [
    // ===== 무기 제작 =====
    {
        id: 'weapon_lv5',
        nameKey: 'forge_recipe_weapon_lv5',
        icon: './swords/5.png',
        category: 'weapon',
        cost: { iron_dust: 20 },
        result: { type: 'weapon', level: 5 },
    },
    {
        id: 'weapon_lv10',
        nameKey: 'forge_recipe_weapon_lv10',
        icon: './swords/10.png',
        category: 'weapon',
        cost: { iron_dust: 15, refining_core: 10 },
        result: { type: 'weapon', level: 10 },
    },
    {
        id: 'weapon_lv15',
        nameKey: 'forge_recipe_weapon_lv15',
        icon: './swords/15.png',
        category: 'weapon',
        cost: { refining_core: 25 },
        result: { type: 'weapon', level: 15 },
    },
    {
        id: 'weapon_lv21',
        nameKey: 'forge_recipe_weapon_lv21',
        descKey: 'forge_recipe_weapon_lv21_desc',
        icon: './swords/21.png',
        category: 'weapon',
        // Lv.21 = 용암정수 30개 (고레벨 파괴 10~15회분)
        cost: { lava_essence: 30 },
        result: { type: 'weapon', level: 21 },
    },

    // ===== 방지권 조합/업그레이드 =====
    {
        id: 'scroll_basic',
        nameKey: 'forge_recipe_scroll_basic',
        icon: './items/mat_iron_dust.png',
        category: 'scroll',
        cost: { iron_dust: 12 },
        result: { type: 'scroll', tier: 0, count: 1 },
    },
    {
        id: 'scroll_mid_from_basic',
        nameKey: 'forge_recipe_scroll_mid',
        icon: './items/mat_refining_core.png',
        category: 'scroll',
        // 초급 3개 + 정련의핵 5개 → 중급 1개
        cost: { refining_core: 8 },
        result: { type: 'scroll', tier: 1, count: 1 },
    },
    {
        id: 'scroll_hi_from_mat',
        nameKey: 'forge_recipe_scroll_hi',
        icon: './items/mat_lava_essence.png',
        category: 'scroll',
        cost: { lava_essence: 10 },
        result: { type: 'scroll', tier: 2, count: 1 },
    },
    {
        id: 'scroll_ultra_from_mat',
        nameKey: 'forge_recipe_scroll_ultra',
        icon: './items/mat_abyss_crystal.png',
        category: 'scroll',
        cost: { lava_essence: 20, abyss_crystal: 5 },
        result: { type: 'scroll', tier: 3, count: 1 },
    },

    // ===== 재료 승급 =====
    {
        id: 'upgrade_iron_to_refining',
        nameKey: 'forge_recipe_upgrade_1',
        icon: './items/mat_refining_core.png',
        category: 'upgrade',
        cost: { iron_dust: 10 },
        result: { type: 'material_upgrade', from: 'iron_dust', to: 'refining_core', gain: 1 },
    },
    {
        id: 'upgrade_refining_to_lava',
        nameKey: 'forge_recipe_upgrade_2',
        icon: './items/mat_lava_essence.png',
        category: 'upgrade',
        cost: { refining_core: 10 },
        result: { type: 'material_upgrade', from: 'refining_core', to: 'lava_essence', gain: 1 },
    },
    {
        id: 'upgrade_lava_to_abyss',
        nameKey: 'forge_recipe_upgrade_3',
        icon: './items/mat_abyss_crystal.png',
        category: 'upgrade',
        cost: { lava_essence: 10 },
        result: { type: 'material_upgrade', from: 'lava_essence', to: 'abyss_crystal', gain: 1 },
    },

    // ===== 버프 아이템 제작 (Sprint 3) =====
    {
        id: 'item_luck_charm',
        nameKey: 'forge_recipe_item_luck_charm',
        descKey: 'forge_recipe_item_luck_charm_desc',
        icon: './items/item_luck_charm.png',
        category: 'item',
        cost: { refining_core: 6 },
        result: { type: 'buff_item', item: 'luck_charm', count: 1 },
    },
    {
        id: 'item_guarantee',
        nameKey: 'forge_recipe_item_guarantee',
        descKey: 'forge_recipe_item_guarantee_desc',
        icon: './items/item_guarantee_ticket.png',
        category: 'item',
        cost: { lava_essence: 8 },
        result: { type: 'buff_item', item: 'guarantee', count: 1 },
    },
    {
        id: 'item_destroy_guard',
        nameKey: 'forge_recipe_item_destroy_guard',
        descKey: 'forge_recipe_item_destroy_guard_desc',
        icon: './items/item_destroy_guard.png',
        category: 'item',
        cost: { refining_core: 10, abyss_crystal: 2 },
        result: { type: 'buff_item', item: 'destroy_guard', count: 1 },
    },
];

// ──────────────────────────────────────────────
//  유틸리티 함수
// ──────────────────────────────────────────────

/**
 * 현재 재료로 레시피를 조합할 수 있는지 확인.
 */
export function canCraft(recipe: ForgeRecipe, materials: Materials): boolean {
    const c = recipe.cost;
    if (c.iron_dust && materials.iron_dust < c.iron_dust) return false;
    if (c.refining_core && materials.refining_core < c.refining_core) return false;
    if (c.lava_essence && materials.lava_essence < c.lava_essence) return false;
    if (c.abyss_crystal && materials.abyss_crystal < c.abyss_crystal) return false;
    return true;
}

/**
 * 재료에서 비용을 차감한 새 Materials 반환 (불변).
 * canCraft 확인 후 호출할 것.
 */
export function deductCost(materials: Materials, cost: MaterialCost): Materials {
    return {
        iron_dust: materials.iron_dust - (cost.iron_dust ?? 0),
        refining_core: materials.refining_core - (cost.refining_core ?? 0),
        lava_essence: materials.lava_essence - (cost.lava_essence ?? 0),
        abyss_crystal: materials.abyss_crystal - (cost.abyss_crystal ?? 0),
    };
}

/**
 * 부족한 재료 목록 반환 (UI 비활성 이유 표시용).
 */
export function getMissingMaterials(
    recipe: ForgeRecipe,
    materials: Materials,
): Array<{ key: keyof Materials; need: number; have: number }> {
    const missing: Array<{ key: keyof Materials; need: number; have: number }> = [];
    const c = recipe.cost;
    const keys: (keyof Materials)[] = ['iron_dust', 'refining_core', 'lava_essence', 'abyss_crystal'];
    for (const key of keys) {
        const need = c[key] ?? 0;
        const have = materials[key];
        if (need > 0 && have < need) {
            missing.push({ key, need, have });
        }
    }
    return missing;
}

/**
 * 재료 비용 텍스트 포맷 (아이콘 없는 버전 — i18n 외부).
 * 예: "용암정수 ×30"
 */
export function formatCostLine(cost: MaterialCost): string {
    const parts: string[] = [];
    if (cost.iron_dust) parts.push(`×${cost.iron_dust}`);
    if (cost.refining_core) parts.push(`×${cost.refining_core}`);
    if (cost.lava_essence) parts.push(`×${cost.lava_essence}`);
    if (cost.abyss_crystal) parts.push(`×${cost.abyss_crystal}`);
    return parts.join('  ');
}
